import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import {
  CartesianGrid, Cell, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis,
} from "recharts";
import { api, API } from "../lib/api";
import { Reveal } from "../lib/ui";

const AXIS = { stroke: "#3f3f46", fontSize: 11, fontFamily: "JetBrains Mono, monospace" };
const usd = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : `$${Math.round(n || 0).toLocaleString()}`);
const int = (n) => Math.round(n || 0).toLocaleString();

const Stat = ({ label, value, note, testId }) => (
  <div className="bg-[#0b0b0b] p-6" data-testid={testId}>
    <p className="overline !text-zinc-500">{label}</p>
    <p className="mono mt-3 text-2xl font-bold text-ochre md:text-3xl">{value}</p>
    {note ? <p className="mt-2 text-[0.68rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p> : null}
  </div>
);

const CurveTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="border border-zinc-700 bg-black/95 p-3 text-xs" data-testid="cost-curve-tooltip">
      <p className="font-display uppercase tracking-tight text-white">{p.county}</p>
      <p className="mono mt-1 text-ochre">{usd(p.cost_per_professional_usd)} per professional</p>
      <p className="mono mt-1 text-zinc-400">{int(p.population)} people · {int(p.players)} players reached</p>
      {p.sweet_spot ? <p className="mt-1 text-maasai">Sweet spot</p> : null}
    </div>
  );
};

export const CountyAllocation = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [pct, setPct] = useState(100);

  useEffect(() => {
    api.get("/county-allocation").then((r) => setD(r.data)).catch(() => setErr("We could not load the county allocation. Refresh to try again."));
  }, []);

  const scenario = useMemo(
    () => d?.scenarios?.find((s) => s.completion_pct === pct) || null,
    [d, pct],
  );
  const rows = useMemo(() => {
    if (!d || !scenario) return [];
    const inside = new Set(scenario.county_names);
    return d.rows.filter((r) => inside.has(r.county));
  }, [d, scenario]);

  if (err)
    return (
      <p className="mt-20 border border-maasai/40 bg-maasai/10 p-6 text-sm text-white" data-testid="allocation-error">
        {err}
      </p>
    );
  if (!d || !scenario) return null;
  const curve = d.cost_curve;

  return (
    <section className="mt-20 border-t border-zinc-900 pt-14" data-testid="county-allocation">
      <Reveal>
        <p className="eyebrow">Sited county by county</p>
        <h2 className="display-2 mt-4 max-w-4xl">Every project has an address</h2>
        <p className="body-copy mt-6 max-w-3xl">
          Nothing in this programme is a national abstraction any more. Each proposed line — courts, pitch upgrades,
          arenas, the training base, service hubs, county academies, the women&rsquo;s pathway and the modular
          stadiums — is allocated to named counties: every eligible county gets an equal floor first, then the
          remainder is shared in proportion to census population, with whole units only. That is why all 47 counties
          carry three courts, an upgraded 11-a-side ground, an academy and a professional stadium, while the
          Priority 18 carry the larger venues. Every shilling of build cost, running cost, job, team and pound of
          social value below is generated from the units sited in that county. Then choose how much of the programme
          actually gets completed.
        </p>
      </Reveal>

      <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="allocation-scenarios">
        <span className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400">Programme completed</span>
        {d.scenarios.map((s) => (
          <button
            key={s.completion_pct}
            type="button"
            onClick={() => setPct(s.completion_pct)}
            data-testid={`allocation-scenario-${s.completion_pct}`}
            className={`border px-4 py-2 text-[0.62rem] font-bold uppercase tracking-[0.16em] transition-colors ${
              pct === s.completion_pct ? "border-maasai bg-maasai text-white" : "border-white/20 text-zinc-300 hover:border-ochre"
            }`}
          >
            {s.completion_pct}%
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="allocation-headline">
        <Stat label="Counties funded" value={int(scenario.counties)} note={`${int(scenario.capex_share_pct)}% of the full build cost`} testId="allocation-counties" />
        <Stat label="Build cost" value={usd(scenario.capex_usd)} note={`${usd(scenario.opex_usd)} a year to run`} testId="allocation-capex" />
        <Stat label="Kenya-wide grassroots value" value={usd(scenario.annual_value_usd)} note={`$${scenario.value_per_dollar} per dollar built`} testId="allocation-value" />
        <Stat label="Players reached a year" value={int(scenario.players)} note={`${int(scenario.operational_jobs)} permanent jobs`} testId="allocation-players" />
        <Stat label="Teams and matches" value={int(scenario.teams)} note={`${int(scenario.matches_per_year)} matches a year`} testId="allocation-teams" />
        <Stat label="Coaching and referee places" value={int(scenario.coaching_places + scenario.referee_places)} note={`${int(scenario.volunteers)} volunteers`} testId="allocation-training" />
        <Stat label="Tax back to the Exchequer" value={usd(scenario.tax_usd)} note="VAT and PAYE, each year" testId="allocation-tax" />
        <Stat label="Years for the value to match the build cost" value={`${scenario.payback_years} years`} note={`${int(scenario.construction_job_years)} build person-years`} testId="allocation-payback" />
      </div>
      <p className="mt-4 max-w-4xl text-xs leading-relaxed text-zinc-400" data-testid="allocation-note">{scenario.note}</p>

      <div className="mt-14">
        <h3 className="display-3">The cost curve</h3>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400">{curve.note}</p>
        <div className="mt-8 h-[420px] w-full border border-zinc-800 bg-[#0b0b0b] p-4" data-testid="cost-curve-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
              <CartesianGrid stroke="#1c1c1c" />
              <XAxis
                type="number"
                dataKey="population"
                name="Population"
                scale="log"
                domain={["auto", "auto"]}
                tick={AXIS}
                tickFormatter={(v) => `${(v / 1e6).toFixed(1)}m`}
                label={{ value: "COUNTY POPULATION (2019 CENSUS)", position: "insideBottom", offset: -18, fill: "#71717a", fontSize: 10 }}
              />
              <YAxis
                type="number"
                dataKey="cost_per_professional_usd"
                name="Cost per professional"
                tick={AXIS}
                tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                width={62}
              />
              <ZAxis type="number" dataKey="players" range={[45, 420]} />
              <Tooltip content={<CurveTip />} />
              <Scatter data={curve.points} name="Counties">
                {curve.points.map((p) => (
                  <Cell
                    key={p.county}
                    fill={p.sweet_spot ? "#E32118" : p.sited ? "#E5A91E" : "#3f3f46"}
                    fillOpacity={p.sweet_spot ? 0.95 : 0.6}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex flex-wrap gap-6 text-[0.62rem] uppercase tracking-[0.16em] text-zinc-400">
          <span className="flex items-center gap-2"><span className="h-3 w-3 bg-maasai" /> Sweet spot</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 bg-ochre" /> Projects sited here</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 bg-zinc-600" /> No units yet</span>
          <span className="mono text-zinc-500" data-testid="cost-curve-sweet">
            {curve.sweet_spot.join(" · ")}
          </span>
        </div>
      </div>

      <div className="mt-14 overflow-x-auto border border-zinc-800" data-testid="allocation-table">
        <table className="w-full min-w-[1100px] border-collapse text-left">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["County", "Units sited", "Build cost", "Running cost", "Trading income", "Jobs", "Players", "Teams", "Annual value", "Value per $1", "Business case"].map((h) => (
                <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.county} className="border-b border-zinc-900" data-testid={`allocation-row-${i}`}>
                <td className="px-4 py-3 text-sm text-white">
                  <Link
                    to={`/counties/${r.county.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    data-testid={`allocation-county-link-${i}`}
                    className="underline-offset-4 hover:text-ochre hover:underline"
                  >
                    {r.county}
                  </Link>
                  {r.priority ? (
                    <span className="ml-2 border border-maasai px-1.5 py-0.5 text-[0.5rem] font-bold uppercase tracking-[0.14em] text-maasai">P18</span>
                  ) : null}
                  <span className="mt-1 block text-[0.6rem] uppercase tracking-[0.12em] text-zinc-500">
                    {r.projects.map((p) => `${p.units} x ${p.item.split(" (")[0].split(",")[0]}`).join(" · ")}
                  </span>
                </td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{r.project_count}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.capex_usd)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.opex_usd)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.annual_revenue_usd)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.operational_jobs)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.players)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.teams)}</td>
                <td className="mono px-4 py-3 text-sm text-ochre">{usd(r.annual_value_usd)}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">${r.value_per_dollar}</td>
                <td className="px-4 py-3">
                  <a
                    href={`${API}/county-prospectus/${encodeURIComponent(r.county)}.pdf`}
                    className="inline-flex items-center gap-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-ochre underline underline-offset-4 hover:text-white"
                    data-testid={`allocation-pdf-${i}`}
                  >
                    <Download size={12} /> PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 max-w-4xl text-xs leading-relaxed text-zinc-400">{d.note}</p>
    </section>
  );
};
